import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';

import {IApiService} from './Interfaces/IApiService';
import {IAuthenticationService} from './Interfaces/IAuthenticationService';
import AuthenticationService from './AuthenticationService';
import {STORAGE_KEYS} from '../Constants/AppConstants';
import {TokenPair} from '../Types/DtoTypes';
import LocalStorageService from './LocalStorageService';
import {ILocalStorageService} from './Interfaces/ILocalStorageService';
import moment from 'moment';
import {User} from 'oidc-client-ts';

class ApiService implements IApiService {
  axiosInstance: AxiosInstance;
  authenticationService: IAuthenticationService;
  storageService: ILocalStorageService;
  realmSignout: () => void;

  lastEndPoint: string;
  refreshCount: number;

  constructor(realmSignout: () => void) {
    this.authenticationService = new AuthenticationService();
    this.realmSignout = realmSignout;

    this.lastEndPoint = '';
    this.refreshCount = 0;

    this.storageService = new LocalStorageService();
    this.axiosInstance = axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(
      config => {
        if (this.lastEndPoint != config.url) {
          this.lastEndPoint = config.url ?? '';
          this.refreshCount = 0;
        } else {
          this.refreshCount += 1;
        }

        let oidcAuth = this.storageService.getOidc();

        if (!oidcAuth) return config;

        let user = User.fromStorageString(oidcAuth);
        if (user.access_token) {
          config.headers['Authorization'] = 'Bearer ' + user.access_token;
          return config;
        } else return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    this.axiosInstance.interceptors.response.use(
      response => {
        return Promise.resolve(response);
      },
      error => {
        const status = error.response ? error.response.status : null;
        if (status === 401) {
          if (this.refreshCount >= 5) return Promise.reject(error);

          let refreshToken = this.storageService.get(
            STORAGE_KEYS.REFRESH_TOKEN_KEY,
          );

          if (refreshToken) {
            return this.authenticationService
              .refresh(refreshToken)
              .then(async (tokenPair: TokenPair) => {
                this.storageService.set(
                  STORAGE_KEYS.TOKEN_KEY,
                  tokenPair.token,
                );
                this.storageService.set(
                  STORAGE_KEYS.REFRESH_TOKEN_KEY,
                  tokenPair.refreshToken,
                );

                return this.axiosInstance.request(error.config);
              })
              .catch((err: any) => {
                //Refresh failed go back to login page
                console.log('refresh fail signout');
                this.realmSignout();
              });
          } else this.realmSignout();
        } else if (status === 403) {
          return Promise.reject(error);
        } else {
          return Promise.reject(error);
        }
      },
    );
  }

  sendRequest<ReturnType>(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<ReturnType>> {
    return this.axiosInstance.request<ReturnType>(config);
  }
}

export default ApiService;
